import { useRef, useState } from 'react';
import { useEditor } from '../../state/editorStore';
import { api, type StockItem } from '../../api/client';
import { playbackClock } from '../../playback/clock';
import { StudioBrollAddDialog } from './StudioBrollAddDialog';

function secs(ms: number): string {
  return `${(ms / 1000).toFixed(2)}s`;
}

/**
 * B-roll tab - upload images/videos and place them on the timeline, either
 * covering the whole frame or as a movable/resizable overlay. Placement/edits
 * write the project's b-roll list; previews live on the stage (drag the overlay
 * to move it, drag its corner to resize). Media is session-only for now.
 */
export function StudioBrollPanel() {
  const brolls = useEditor((s) => s.brolls);
  const durationMs = useEditor((s) => s.durationMs);
  const selectedId = useEditor((s) => s.selectedBrollId);
  const addBroll = useEditor((s) => s.addBroll);
  const updateBroll = useEditor((s) => s.updateBroll);
  const removeBroll = useEditor((s) => s.removeBroll);
  const selectBroll = useEditor((s) => s.selectBroll);
  const fileRef = useRef<HTMLInputElement>(null);
  const [dlgOpen, setDlgOpen] = useState(false);

  const dur = Math.max(durationMs, brolls.at(-1)?.startMs ?? 0, 1000);

  // Add a chosen stock clip: the server ingests + stores it, we drop it on the
  // timeline with its presigned preview URL and durable storage key.
  const onPickStock = async (item: StockItem) => {
    const projectId = useEditor.getState().projectId;
    if (!projectId) return;
    const added = await api.stockAdd(projectId, {
      source: item.source,
      kind: item.kind,
      downloadUrl: item.downloadUrl,
      name: item.author,
      durationSec: item.durationSec,
    });
    const id = addBroll(added.kind, added.name, added.url, playbackClock.timeMs());
    updateBroll(id, { storageKey: added.storageKey, ...(added.durationMs ? { durationMs: added.durationMs } : {}) });
  };

  const onPick = async (file: File | undefined) => {
    if (!file) return;
    const kind = file.type.startsWith('video') ? 'video' : 'image';
    const name = file.name.replace(/\.[^.]+$/, '');
    // Show it immediately from a local URL, then upload in the background so it
    // gets a durable storageKey and survives refresh (persisted on autosave).
    const id = addBroll(kind, name, URL.createObjectURL(file), playbackClock.timeMs());
    const projectId = useEditor.getState().projectId;
    if (!projectId) return; // demo mode: session-only
    try {
      const { storageKey, url } = await api.brollUpload(projectId, file.name, file.type, file.size);
      await fetch(url, { method: 'PUT', headers: { 'Content-Type': file.type }, body: file });
      updateBroll(id, { storageKey });
    } catch {
      /* upload failed - the clip stays for this session but won't persist */
    }
  };

  return (
    <div className="slp-body shk">

      <input
        ref={fileRef}
        type="file"
        accept="image/*,video/*"
        hidden
        onChange={(e) => onPick(e.target.files?.[0] ?? undefined)}
      />
      <button className="sau-add" onClick={() => setDlgOpen(true)}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="M4 5h16v14H4zM4 15l4-4 4 4 3-3 5 5" />
          <circle cx="9" cy="9" r="1.5" fill="currentColor" stroke="none" />
        </svg>
        Add image or video
      </button>

      <StudioBrollAddDialog
        open={dlgOpen}
        onClose={() => setDlgOpen(false)}
        onUpload={() => fileRef.current?.click()}
        onPickStock={onPickStock}
      />

      <span className="str-section">On timeline{brolls.length ? ` · ${brolls.length}` : ''}</span>
      {brolls.length === 0 ? (
        <div className="str-empty">No B-roll yet. Add one above.</div>
      ) : (
        <div className="str-list">
          {brolls.map((b) => {
            const on = b.id === selectedId;
            return (
              <div key={b.id} className={on ? 'str-item on' : 'str-item'}>
                <button className="str-item-head" onClick={() => { selectBroll(b.id); playbackClock.seek(b.startMs); }}>
                  <span className={`brl-badge brl-${b.kind}`}>{b.kind === 'video' ? '▶' : '🖼'}</span>
                  <span className="str-item-name">{b.name}</span>
                  <span className="str-item-time">{secs(b.startMs)} → {secs(b.startMs + b.durationMs)}</span>
                </button>
                {on && (
                  <div className="str-edit">
                    <div className="shk-row">
                      <span>Placement</span>
                      <div className="ses-seg">
                        {(['cover', 'overlay'] as const).map((m) => (
                          <button key={m} className={b.mode === m ? 'on' : ''} onClick={() => updateBroll(b.id, { mode: m })}>
                            {m === 'cover' ? 'Full' : 'Overlay'}
                          </button>
                        ))}
                      </div>
                    </div>
                    <Slider label="Start" min={0} max={dur} step={10} value={b.startMs}
                      onChange={(v) => updateBroll(b.id, { startMs: v })} display={secs(b.startMs)} />
                    <Slider label="Duration" min={200} max={Math.max(1000, dur)} step={50} value={b.durationMs}
                      onChange={(v) => updateBroll(b.id, { durationMs: v })} display={secs(b.durationMs)} />
                    {b.mode === 'overlay' && (
                      <>
                        <Slider label="Size" min={0.1} max={1} step={0.01} value={b.scale}
                          onChange={(v) => updateBroll(b.id, { scale: v })} display={`${Math.round(b.scale * 100)}%`} />
                        <Slider label="X" min={0} max={1} step={0.01} value={b.x}
                          onChange={(v) => updateBroll(b.id, { x: v })} display={`${Math.round(b.x * 100)}%`} />
                        <Slider label="Y" min={0} max={1} step={0.01} value={b.y}
                          onChange={(v) => updateBroll(b.id, { y: v })} display={`${Math.round(b.y * 100)}%`} />
                      </>
                    )}
                    <div className="shk-row">
                      <span>Fit</span>
                      <div className="ses-seg">
                        {(['cover', 'contain'] as const).map((f) => (
                          <button key={f} className={b.fit === f ? 'on' : ''} onClick={() => updateBroll(b.id, { fit: f })}>
                            {f === 'cover' ? 'Fill' : 'Fit'}
                          </button>
                        ))}
                      </div>
                    </div>
                    <button className="str-remove" onClick={() => removeBroll(b.id)}>Remove</button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Slider({ label, min, max, step, value, onChange, display }: {
  label: string; min: number; max: number; step: number; value: number; onChange: (v: number) => void; display: string;
}) {
  return (
    <div className="shk-row">
      <span>{label}</span>
      <div className="shk-size">
        <input type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(Number(e.target.value))} />
        <span>{display}</span>
      </div>
    </div>
  );
}
